/**
 * AdvisorSearchDropdown Component (T048 - US3)
 * Live search dropdown for finding financial advisors
 *
 * Features:
 * - Search input with 300ms debounce (FR-020)
 * - Minimum 2 characters required for search (FR-017)
 * - Live results dropdown with advisor cards
 * - Empty state with helpful prompt
 * - No-results state with "Contact us" button (FR-035)
 * - Skeleton loading state
 *
 * Styling based on docs/prototypes/SelectAdvisorPage.tsx
 *
 * FR-016, FR-017, FR-018, FR-019, FR-020, FR-035
 */

import { useState } from 'react';
import type { AdvisorInfo } from '../types';
import { useAdvisorSearch } from '../hooks/useAdvisorSelection';
import { AdvisorCard } from './AdvisorCard';
import { Button } from '@/shared/components/ui/button';

type AdvisorSearchDropdownProps = {
  onSelect: (advisor: AdvisorInfo) => void;
};

export const AdvisorSearchDropdown = ({ onSelect }: AdvisorSearchDropdownProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const { data: searchData, isLoading, debouncedQuery } = useAdvisorSearch(searchQuery);

  const advisors = searchData?.data ?? [];
  const isDebouncing = searchQuery !== debouncedQuery;
  const showResults = debouncedQuery.trim().length >= 2;
  const hasResults = advisors.length > 0;

  const handleAdvisorSelect = (advisor: AdvisorInfo) => {
    onSelect(advisor);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleContactUs = () => {
    // Open existing support modal (same as matchmaking no-results state)
    // This would typically trigger a modal or redirect to a support page
    // For now, we'll open the support URL in a new tab
    const supportUrl = import.meta.env.VITE_ADVISOR_SUPPORT_URL;
    if (supportUrl) {
      window.open(supportUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="w-full relative">
      {/* Search input trigger */}
      <div
        onClick={() => setIsOpen(true)}
        className="input input-lg rounded-md border-2 border-gray-600 bg-white flex items-center justify-between w-full min-h-[3.5rem] px-4 py-3 text-lg hover:border-[#026857] focus-within:border-[#026857] transition-colors cursor-pointer"
      >
        {!isOpen ? (
          <>
            <span className="text-gray-600 font-medium text-lg">
              Search for your financial advisor...
            </span>
            <svg
              className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''} text-gray-400`}
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M6.293 8.293a1 1 0 0 1 1.414 0L10 10.586l2.293-2.293a1 1 0 1 1 1.414 1.414l-3 3a1 1 0 0 1-1.414 0l-3-3a1 1 0 0 1 0-1.414z"
              />
            </svg>
          </>
        ) : (
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full text-gray-700 placeholder-gray-400 focus:outline-none"
            autoFocus
          />
        )}
      </div>

      {/* Dropdown results */}
      {isOpen && (
        <div className="absolute z-50 mt-2 w-full bg-white shadow-lg border border-gray-200 rounded-md">
          <div className="max-h-96 overflow-y-auto px-2 py-2">
            {/* Empty initial state */}
            {!showResults && (
              <div className="py-6 text-center text-gray-600">
                Start typing to search for your financial advisor...
              </div>
            )}

            {/* Loading state */}
            {(isLoading || isDebouncing) && showResults && (
              <div className="py-4 text-center" data-testid="loading-skeleton">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#026857] mx-auto"></div>
                <p className="mt-2 text-gray-600">Searching...</p>
              </div>
            )}

            {/* No results state */}
            {showResults && !isLoading && !isDebouncing && !hasResults && (
              <div className="py-6 text-center">
                <p className="text-gray-600 mb-4">No advisors found.</p>
                <Button
                  type="button"
                  onClick={handleContactUs}
                  className="bg-[#026857] hover:bg-[#024d42] text-white font-termina px-6 py-2 rounded-md transition-colors"
                >
                  Can't find your advisor? Contact us
                </Button>
              </div>
            )}

            {/* Results list */}
            {showResults && !isLoading && !isDebouncing && hasResults && (
              <ul role="list" className="m-0 p-0">
                {advisors.map((advisor) => (
                  <li key={advisor.id} className="p-0" data-testid="advisor-card">
                    <AdvisorCard advisor={advisor} onClick={handleAdvisorSelect} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Backdrop to close dropdown when clicking outside */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setIsOpen(false);
            setSearchQuery('');
          }}
        />
      )}
    </div>
  );
};
